import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { BannerInfoItem, Testimonial } from '@app/models/_index';
import {
  CardTypeCallToActions,
  ProductShortName,
  RouteTypes,
  TestimonialViewTypes,
} from '@app/enums/_index';
import { HasPageTitleAndMetaData, TestimonialInterface } from '@app/interfaces/_index';
import { Meta, Title } from '@angular/platform-browser';
import { Subscription, delay, of } from 'rxjs';
import { WordpressTestimonialsService } from '@app/services/_index';

import { BannerInfoFactory } from '@app/factories/banner-info-item.factory';
import { TestimonialAdapter } from '@app/adapters/_index';
import { environment } from '@environments/environment';
import { scrollToPosition } from '@app/utilities/scrolling.utilities';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy, HasPageTitleAndMetaData {
  sub: Subscription;
  isLoading: boolean;
  testimonials: Testimonial[] = [];
  images: BannerInfoItem[] = [];
  infoItem: BannerInfoItem;
  showHomePageBannerCarousel: boolean;
  subscriptionTilesNew = environment.beyond.featureFlags.subscriptionTilesNew;
  showTrustedPartner = environment.beyond.featureFlags.showTrustedPartner;
  showCourseFeature = environment.beyond.featureFlags.showCourseFeature;
  showContactSales = environment.beyond.featureFlags.showContactSales;
  homeBanner = environment.beyond.featureFlags.homeBannerNew;
  contactSalesForm = environment.beyond.featureFlags.contactSalesForm;
  showHomeVideoSection = environment.beyond.featureFlags.showHomeVideoSection;

  @Input() isLightMode = false; // Toggle to change background styling from light or dark

  tiles = [
    {
      title: 'Video Library',
      description:
        'Lorem ipsum, or lipsum as it is sometimes known, is dummy text used in laying out print, graphic or web designs. The passage is attributed to an unknown typesetter. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt.',
      link: '/explore/videos',
      mediaUrl: '/assets/img/video-library.png',
      mediaType: 'image',
    },
    {
      title: 'Certifications',
      description:
        'Genesys offers different levels of certifications such as Professional, Developer, and Specialist across various Genesys product platforms. Genesys Beyond certifications are recognized globally and have a proven track record for supporting high-quality performance.',
      link: '/explore/certification/home',
      mediaUrl: '/assets/img/certification.png',
      mediaType: 'image',
    },
    {
      title: 'Redeem Voucher',
      description:
        'Browse through an extensive catalog of virtual instructor-led, self-study, and public instructor-led courses for each Genesys product. Find courses that are unique to your role, including training for business users, system administrators, and developers.',
      link: '/explore/voucher-redemption',
      mediaUrl: '/assets/img/redeem.png',
      mediaType: 'image',
    },
    /* {
      title: 'Tutorial Video',
      description: 'Watch this tutorial video',
      link: '/explore/tutorial',
      mediaType: 'video',
      embedUrl: 'https://fast.wistia.net/embed/iframe/r1u9h61so1?videoFoam=true',
      mediaUrl: '',
    },*/
  ];

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private testimonialAdapter: TestimonialAdapter,
    private wpTestimonialService: WordpressTestimonialsService,
    private bannerInfoFactory: BannerInfoFactory
  ) {}

  /**
   * Default onInit
   */
  ngOnInit(): void {
    // Build the collection from banner info factory.
    this.images = this.bannerInfoFactory.getCollection();
    this.showHomePageBannerCarousel = environment.beyond.featureFlags.showHomePageBannerCarousel;
    if (!this.showHomePageBannerCarousel) {
      this.infoItem = this.bannerInfoFactory.getBannerInfo(ProductShortName.BEYOND);
    }
    // Set page title and metadata
    this.setTitleAndMetaData();
    // Get testimonials
    this.getTestimonials();
  }

  /**
   * On after view init, check the contact sales history to reset and scroll into position
   * for the contact sales outside of home page
   */
  ngAfterViewInit(): void {
    const contactsalesHistory = JSON.parse(localStorage.getItem('contactsales-scroll'));
    if (contactsalesHistory === true) {
      this.sub = of(true)
        .pipe(delay(1000))
        .subscribe(() => {
          scrollToPosition(-160, 'contactsales');
        });
    }
    localStorage.removeItem('contactsales-scroll');
  }

  /**
   * On Destroy unsubscribe from testimonial service to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  /**
   * Return home page testimonials
   */
  getTestimonials(): void {
    // Default word press CMS
    this.isLoading = true;
    this.sub = this.wpTestimonialService
      .getTestimonialsByView(TestimonialViewTypes.HOME)
      .subscribe({
        next: (testimonials: TestimonialInterface[]) => {
          this.isLoading = false;
          if (testimonials) {
            testimonials.forEach((item: TestimonialInterface) => {
              if (item) {
                const testimonial = this.testimonialAdapter.filter(item, TestimonialViewTypes.HOME);
                if (testimonial) {
                  this.testimonials.push(testimonial);
                }
              }
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Home Testimonials content completed');
        },
      });
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   * Set page title and metadata tags
   */
  setTitleAndMetaData(): void {
    // Set title for default main page
    this.titleService.setTitle('Education');
    // Set meta tag for description for beyond
    this.metaService.updateTag({
      name: 'description',
      content:
        'Learn new technologies and the necessary business skills to grow in the world of customer experience, including artificial intelligence, workforce management and more.',
    });
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }
}
